// Header.tsx avec Elections 2025 mis en avant (version simple)
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useGTMTracking } from '../hooks/useGTMTracking';

interface MenuItem {
    label: string;
    href?: string;
    to?: string;
    submenu?: MenuItem[];
    highlight?: boolean; // Nouvelle propriété pour mettre en avant
}

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [logoError, setLogoError] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const {
        trackAdhererClick,
        trackEspaceMembreClick,
        trackMenuClick,
        trackClick
    } = useGTMTracking();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    }, [location]);

    useEffect(() => {
        if (location.hash && location.pathname === '/') {
            setTimeout(() => {
                scrollToSection(location.hash);
            }, 100);
        }
    }, [location]);

    // Menu items
    const menuItems: MenuItem[] = [
        { label: 'Accueil', to: '/' },
        { label: 'Mot du Président', href: '#president-message' },
        {
            label: 'Notre Parti',
            submenu: [
                { label: 'Notre Vision', href: '#vision' },
                { label: 'Programme complet', to: '/vision' },
            ]
        },
    ];

    const scrollToSection = (sectionId: string) => {
        const element = document.querySelector(sectionId);
        if (element) {
            const headerHeight = 80;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleSectionScroll = (sectionId: string) => {
        if (location.pathname !== '/') {
            navigate(`/${sectionId}`);
            return;
        }
        scrollToSection(sectionId);
    };

    const renderMenuItem = (item: MenuItem) => {
        // ← CLASSES SIMPLIFIÉES - juste font-bold pour les éléments mis en avant
        const highlightClasses = item.highlight
            ? 'text-primary-blue font-bold'
            : 'text-gray-700';

        const highlightHoverClasses = 'hover:text-primary-blue';

        // Si l'item a un sous-menu
        if (item.submenu) {
            return (
                <div className="relative group">
                    <button
                        className={`flex items-center space-x-1 transition-colors py-2 font-medium text-sm ${highlightClasses} ${highlightHoverClasses}`}
                        onMouseEnter={() => setActiveDropdown(item.label)}
                        onMouseLeave={() => setActiveDropdown(null)}
                    >
                        <span className="relative">
                            {item.label}
                            {/* ← GARDE SEULEMENT LE POINT ROUGE CLIGNOTANT */}
                            {item.highlight && (
                                <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                            )}
                        </span>
                        <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Sous-menu dropdown */}
                    <div
                        className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 transition-all duration-200 ${
                            activeDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                        }`}
                        onMouseEnter={() => setActiveDropdown(item.label)}
                        onMouseLeave={() => setActiveDropdown(null)}
                    >
                        {item.submenu.map((subItem) => (
                            <div key={subItem.label}>
                                {subItem.to ? (
                                    <Link
                                        to={subItem.to}
                                        onClick={() => {
                                            trackMenuClick(subItem.label, subItem.to!);
                                            setActiveDropdown(null);
                                        }}
                                        className={`block px-4 py-2 text-sm transition-colors hover:bg-gray-50 ${
                                            location.pathname === subItem.to ? 'text-primary-blue bg-blue-50' : 'text-gray-700 hover:text-primary-blue'
                                        }`}
                                    >
                                        {subItem.label}
                                    </Link>
                                ) : (
                                    <button
                                        onClick={() => {
                                            trackMenuClick(subItem.label, subItem.href!);
                                            handleSectionScroll(subItem.href!);
                                            setActiveDropdown(null);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-primary-blue hover:bg-gray-50 transition-colors"
                                    >
                                        {subItem.label}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Item normal (sans sous-menu)
        if (item.to) {
            return (
                <Link
                    to={item.to}
                    onClick={() => {
                        trackMenuClick(item.label, item.to!);
                    }}
                    className={`flex items-center space-x-1 transition-colors py-2 font-medium text-sm relative ${
                        location.pathname === item.to ? 'text-primary-blue' : highlightClasses
                    } ${highlightHoverClasses}`}
                >
                    <span className="relative">
                        {item.label}
                        {item.highlight && (
                            <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                        )}
                    </span>
                </Link>
            );
        }

        return (
            <button
                onClick={() => {
                    trackMenuClick(item.label, item.href!);
                    handleSectionScroll(item.href!);
                }}
                className={`flex items-center space-x-1 transition-colors py-2 font-medium text-sm relative ${highlightClasses} ${highlightHoverClasses}`}
            >
                <span className="relative">
                    {item.label}
                    {item.highlight && (
                        <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                    )}
                </span>
            </button>
        );
    };

    const handleDropdownToggle = (label: string) => {
        setActiveDropdown(activeDropdown === label ? null : label);
    };

    const handleMenuItemClick = (item: MenuItem) => {
        setIsMenuOpen(false);
        setActiveDropdown(null);

        if (item.href) {
            trackMenuClick(item.label + '_mobile', item.href);
            handleSectionScroll(item.href);
        } else if (item.to) {
            trackMenuClick(item.label + '_mobile', item.to);
        }
    };

    const handleSubMenuItemClick = (subItem: MenuItem, parentLabel: string) => {
        setIsMenuOpen(false);
        setActiveDropdown(null);

        if (subItem.href) {
            trackMenuClick(`${parentLabel}_${subItem.label}_mobile`, subItem.href);
            handleSectionScroll(subItem.href);
        } else if (subItem.to) {
            trackMenuClick(`${parentLabel}_${subItem.label}_mobile`, subItem.to);
        }
    };

    const handleAdhererClick = (source: 'header' | 'menu_mobile') => {
        trackAdhererClick(source);
    };

    const handleEspaceMembreClick = (source: 'header' | 'menu_mobile') => {
        trackEspaceMembreClick(source);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isHomePage = location.pathname === '/';

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
            }`}
        >
            <nav className="border-b border-gray-200">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        <Link
                            to="/"
                            className="flex items-center"
                            onClick={() => {
                                trackClick('logo', 'header');
                            }}
                        >
                            <div className="w-12 h-12 relative">
                                <img
                                    src="/logo128.png"
                                    alt="UDB Logo"
                                    className="w-12 h-12 object-contain relative z-10"
                                    onError={() => setLogoError(true)}
                                    onLoad={() => setLogoError(false)}
                                />
                            </div>
                            <div className="ml-3">
                                <h1 className="text-lg font-semibold text-gray-900">Union Démocratique des Bâtisseurs</h1>
                            </div>
                        </Link>

                        {/* Navigation desktop */}
                        <div className="hidden lg:flex items-center space-x-6">
                            {menuItems.map((item) => (
                                <div key={item.label} className="relative group">
                                    {renderMenuItem(item)}
                                </div>
                            ))}
                        </div>

                        <div className="hidden md:flex items-center space-x-3">
                            <Link
                                to="/adhesion"
                                onClick={() => handleAdhererClick('header')}
                                className="bg-primary-green text-white px-5 py-2 text-sm font-semibold hover:bg-udb-green-700 transition-all rounded"
                            >
                                ADHÉRER
                            </Link>
                            <Link
                                to="/membre"
                                onClick={() => handleEspaceMembreClick('header')}
                                className="bg-primary-blue text-white px-5 py-2 text-sm font-semibold hover:bg-blue-700 transition-all rounded"
                            >
                                ESPACE MEMBRE
                            </Link>
                        </div>

                        <button
                            className="lg:hidden p-2 hover:bg-gray-100 transition-colors rounded"
                            onClick={() => {
                                setIsMenuOpen(!isMenuOpen);
                                trackClick('mobile_menu_toggle', 'header');
                            }}
                        >
                            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Menu mobile */}
                {isMenuOpen && (
                    <div className="lg:hidden bg-white border-t border-gray-200">
                        <div className="container mx-auto px-4 py-4">
                            {menuItems.map((item) => (
                                <div key={item.label} className="border-b border-gray-100 last:border-b-0">
                                    {item.submenu ? (
                                        // Item avec sous-menu
                                        <div>
                                            <button
                                                onClick={() => handleDropdownToggle(item.label)}
                                                className={`flex items-center justify-between w-full py-3 transition-colors font-medium relative ${
                                                    item.highlight
                                                        ? 'text-primary-blue font-bold'
                                                        : 'text-gray-700 hover:text-primary-blue'
                                                }`}
                                            >
                                                <span className="relative">
                                                    {item.label}
                                                    {item.highlight && (
                                                        <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                                                    )}
                                                </span>
                                                <svg
                                                    className={`w-4 h-4 transition-transform ${
                                                        activeDropdown === item.label ? 'rotate-180' : ''
                                                    }`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>

                                            {/* Sous-menu mobile */}
                                            {activeDropdown === item.label && (
                                                <div className="pl-4 pb-2">
                                                    {item.submenu.map((subItem) => (
                                                        <div key={subItem.label} className="py-2">
                                                            {subItem.to ? (
                                                                <Link
                                                                    to={subItem.to}
                                                                    className={`text-sm transition-colors ${
                                                                        location.pathname === subItem.to
                                                                            ? 'text-primary-blue font-medium'
                                                                            : 'text-gray-600 hover:text-primary-blue'
                                                                    }`}
                                                                    onClick={() => handleSubMenuItemClick(subItem, item.label)}
                                                                >
                                                                    {subItem.label}
                                                                </Link>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleSubMenuItemClick(subItem, item.label)}
                                                                    className="text-sm text-gray-600 hover:text-primary-blue transition-colors w-full text-left"
                                                                >
                                                                    {subItem.label}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        // Item normal
                                        <div className="py-3">
                                            {item.to ? (
                                                <Link
                                                    to={item.to}
                                                    className={`transition-colors font-medium relative ${
                                                        location.pathname === item.to
                                                            ? 'text-primary-blue'
                                                            : item.highlight
                                                                ? 'text-primary-blue font-bold'
                                                                : 'text-gray-700 hover:text-primary-blue'
                                                    }`}
                                                    onClick={() => handleMenuItemClick(item)}
                                                >
                                                    <span className="relative">
                                                        {item.label}
                                                        {item.highlight && (
                                                            <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                                                        )}
                                                    </span>
                                                </Link>
                                            ) : (
                                                <button
                                                    onClick={() => handleMenuItemClick(item)}
                                                    className={`transition-colors font-medium w-full text-left relative ${
                                                        item.highlight
                                                            ? 'text-primary-blue font-bold'
                                                            : 'text-gray-700 hover:text-primary-blue'
                                                    }`}
                                                >
                                                    <span className="relative">
                                                        {item.label}
                                                        {item.highlight && (
                                                            <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></span>
                                                        )}
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <div className="flex flex-col space-y-3 mt-4 pt-4 border-t border-gray-200">
                                <Link
                                    to="/adhesion"
                                    className="bg-primary-green text-white px-6 py-2.5 text-sm font-semibold text-center hover:bg-udb-green-700 transition-all rounded"
                                    onClick={() => {
                                        handleAdhererClick('menu_mobile');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    ADHÉRER
                                </Link>
                                <Link
                                    to="/membre"
                                    className="bg-primary-blue text-white px-6 py-2.5 text-sm font-semibold text-center hover:bg-blue-700 transition-all rounded"
                                    onClick={() => {
                                        handleEspaceMembreClick('menu_mobile');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    ESPACE MEMBRE
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
